// Network (Ethernet / Wi-Fi) thermal-printer transport. When the Print Settings
// printer type is "Ethernet", receipts go to the billing printer's IP and KOTs
// are routed per department (service point) to each station's printer IP, with
// the default KOT printer IP as the fallback. Streams raw ESC/POS over a TCP
// socket via the native NetPrinterPlugin; only available inside the Android app
// (the WebView can't open raw sockets), so browsers fall back to window.print().

import { getPrintSettingsSync } from './db';
import { isNativeApp, netPrint } from './nativeNet';

const DEFAULT_PORT = 9100;

// The TCP port from settings, falling back to the 9100 RAW/JetDirect default.
function netPort(): number {
  const p = parseInt(getPrintSettingsSync().ethernetPort, 10);
  return Number.isFinite(p) && p > 0 && p <= 65535 ? p : DEFAULT_PORT;
}

// True when Ethernet is the chosen printer type and we're running natively.
export function ethernetSelected(): boolean {
  return isNativeApp() && getPrintSettingsSync().printerType === 'Ethernet';
}

// True when Ethernet printing is set up for the billing/receipt printer.
export function netReceiptConfigured(): boolean {
  return ethernetSelected() && !!getPrintSettingsSync().billingIp.trim();
}

// The configured printer IP for a department (service point), if any.
export function departmentIp(servicePointName: string): string | undefined {
  const ip = getPrintSettingsSync().departmentIps?.[servicePointName]?.trim();
  return ip || undefined;
}

// The default KOT printer IP, if any.
export function kotIp(): string | undefined {
  const ip = getPrintSettingsSync().kotIp.trim();
  return ip || undefined;
}

// Send raw ESC/POS bytes to a specific printer IP over TCP.
export async function printNetBytes(ip: string, data: Uint8Array): Promise<void> {
  if (!isNativeApp()) throw new Error('Network printing is only available in the Android app');
  const target = ip.trim();
  if (!target) throw new Error('No printer IP configured');
  await netPrint(target, netPort(), data);
}

// Send raw ESC/POS bytes to the configured billing/receipt printer IP.
export async function printNetReceipt(data: Uint8Array): Promise<void> {
  await printNetBytes(getPrintSettingsSync().billingIp, data);
}

// Send a test page to an explicit ip:port — used by the Print Settings screen
// to verify an IP the user just typed (before it has been saved).
export async function printNetTest(ip: string, port: string, data: Uint8Array): Promise<void> {
  if (!isNativeApp()) throw new Error('Network printing is only available in the Android app');
  const target = ip.trim();
  if (!target) throw new Error('Enter a printer IP first');
  const p = parseInt(port, 10);
  await netPrint(target, Number.isFinite(p) && p > 0 && p <= 65535 ? p : DEFAULT_PORT, data);
}
